import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { cn } from "../../../lib/utils"

const inputVariants = cva(
  "file:text-foreground placeholder:text-muted-foreground/60 selection:bg-primary selection:text-primary-foreground dark:bg-input/30 w-full min-w-0 bg-transparent font-black transition-[color,box-shadow,border-radius] outline-none file:inline-flex file:border-0 file:bg-transparent file:text-sm file:font-black disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive placeholder:font-normal",
  {
    variants: {
      variant: {
        default: "border-input border-[1.5px]",
        ghost: "border-transparent hover:bg-accent/50",
      },
      size: {
        sm: "h-10 rounded-[14px] px-4 py-1.5 text-sm file:h-7",
        md: "h-12 rounded-[18px] px-5 py-2 text-base md:text-sm file:h-8",
        lg: "h-16 rounded-[24px] px-6 py-2.5 text-lg md:text-base file:h-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
)

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  /**
   * Icon to display at the start of the input
   */
  leadingIcon?: React.ReactNode
  /**
   * Icon to display at the end of the input
   */
  trailingIcon?: React.ReactNode
  /**
   * If true, shows a clear button when input has value
   */
  clearable?: boolean
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void
  /**
   * If true, the input will take up the full width of its container
   */
  fullWidth?: boolean
  /**
   * Wrapper className for the input container
   */
  wrapperClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      type,
      variant,
      size,
      leadingIcon,
      trailingIcon,
      clearable,
      onClear,
      fullWidth = true,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(defaultValue ?? "")
    const isControlled = value !== undefined
    const currentValue = isControlled ? value : internalValue

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value)
      }
      onChange?.(e)
    }

    const handleClear = () => {
      if (!isControlled) {
        setInternalValue("")
      }
      onClear?.()
    }

    const hasValue = currentValue !== undefined && currentValue !== ""
    const showClearButton = clearable && hasValue && !props.disabled

    // Simple input without wrapper
    if (!leadingIcon && !trailingIcon && !clearable) {
      return (
        <input
          ref={ref}
          type={type}
          data-slot="input"
          data-variant={variant}
          data-size={size}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          className={cn(
            inputVariants({ variant, size }),
            !fullWidth && "w-auto",
            className
          )}
          {...props}
        />
      )
    }

    // Input with wrapper for icons/clearable
    const iconSizeClass = size === "sm" ? "size-4" : size === "lg" ? "size-6" : "size-5"
    const leftPadding = leadingIcon
      ? size === "sm"
        ? "pl-11"
        : size === "lg"
          ? "pl-16"
          : "pl-13"
      : undefined
    const rightPadding =
      trailingIcon || showClearButton
        ? size === "sm"
          ? "pr-11"
          : size === "lg"
            ? "pr-16"
            : "pr-13"
        : undefined

    return (
      <div
        data-slot="input-wrapper"
        className={cn(
          "relative",
          fullWidth ? "w-full" : "w-auto inline-block",
          wrapperClassName
        )}
      >
        {leadingIcon && (
          <span
            data-slot="input-leading-icon"
            className={cn(
              "text-muted-foreground pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 [&_svg]:shrink-0",
              `[&_svg]:${iconSizeClass}`
            )}
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          type={type}
          data-slot="input"
          data-variant={variant}
          data-size={size}
          value={isControlled ? value : internalValue}
          onChange={handleChange}
          className={cn(
            inputVariants({ variant, size }),
            leftPadding,
            rightPadding,
            className
          )}
          {...props}
        />
        {showClearButton && !trailingIcon && (
          <button
            type="button"
            data-slot="input-clear-button"
            onClick={handleClear}
            className={cn(
              "text-muted-foreground hover:text-foreground absolute right-4 top-1/2 -translate-y-1/2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
            aria-label="Clear input"
          >
            <X className={iconSizeClass} />
          </button>
        )}
        {trailingIcon && (
          <span
            data-slot="input-trailing-icon"
            className={cn(
              "text-muted-foreground pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 [&_svg]:shrink-0",
              `[&_svg]:${iconSizeClass}`
            )}
          >
            {trailingIcon}
          </span>
        )}
      </div>
    )
  }
)

Input.displayName = "Input"

export { Input }
